/**
 * A4 — Duración personalizada por cliente y servicio (barber_clients.custom_durations).
 * 5 min – 4 h en pasos de 5; «Normal» = duración del catálogo. Aplica en huecos online, app y Claude.
 */
import { useEffect, useState } from 'react';
import { Timer } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { clientsApi } from '@/lib/clientsApi';
import { useAuth } from '@/contexts/AuthContext';
import { tr } from '@/contexts/LanguageContext';

const OPTIONS = Array.from({ length: 48 }, (_, i) => (i + 1) * 5);
const fmt = (m) => (m >= 60 ? `${Math.floor(m / 60)} h${m % 60 ? ` ${m % 60} min` : ''}` : `${m} min`);

export const ClientDurations = ({ clientId, durations = {}, onChanged }) => {
  const { user } = useAuth();
  const [services, setServices] = useState(null);
  const [busy, setBusy] = useState(null);
  useEffect(() => {
    if (!user?.username) return;
    api.get(`/barbers/${encodeURIComponent(user.username)}/services`).then((r) => setServices((r?.items || r || []).filter((s) => s.active !== false))).catch(() => setServices([]));
  }, [user?.username]);

  const save = async (serviceId, value) => {
    setBusy(serviceId);
    try {
      const r = await clientsApi.setCustomDuration(clientId, serviceId, value === 'normal' ? null : Number(value));
      onChanged?.(r);
      toast.success(value === 'normal' ? tr("Duración normal restablecida") : tr("Duración personalizada guardada"));
    } catch (e) { toast.error(e.message); } finally { setBusy(null); }
  };

  if (!services) return null;
  return (
    <div className="rounded-2xl border border-border p-3 space-y-2" data-testid="client-durations">
      <div className="flex items-center gap-2">
        <Timer className="w-4 h-4 text-primary" />
        <p className="text-sm font-semibold">{tr("Duración personalizada")}</p>
      </div>
      <p className="text-[11px] text-muted-foreground">{tr("Si este cliente necesita más o menos tiempo, ajusta la duración por servicio. Se aplica en sus reservas online, en tu agenda y en Claude.")}</p>
      {services.length === 0 && <p className="text-[11px] text-muted-foreground" data-testid="client-durations-empty">{tr("Aún no tienes servicios activos.")}</p>}
      {services.map((s) => {
        const cur = durations?.[s.id];
        return (
          <label key={s.id} className="flex items-center gap-2 text-xs" data-testid={`client-duration-row-${s.id}`}>
            <span className="flex-1 min-w-0 truncate">{s.name} <span className="text-muted-foreground">· {tr("normal {0}", fmt(s.duration_minutes || 30))}</span></span>
            <select value={cur ? String(cur) : 'normal'} disabled={busy === s.id} onChange={(e) => save(s.id, e.target.value)}
                    className={`h-8 rounded-lg border px-2 bg-background text-xs ${cur ? 'border-primary text-primary font-semibold' : 'border-border text-foreground'}`}
                    data-testid={`client-duration-select-${s.id}`}>
              <option value="normal">{tr("Normal")}</option>
              {OPTIONS.map((m) => <option key={m} value={m}>{fmt(m)}</option>)}
            </select>
          </label>
        );
      })}
    </div>
  );
};

export default ClientDurations;
